<template>
    <div    class="">
        <!-- Company Permission Level -->
        <div    v-if="canEditUser"
                class="mt-4 sm:mt-0">
            <div class="md:grid md:grid-cols-4 md:gap-6">
                <div class="lg:pl-8 md:col-span-1">
                    <div class="flex flex-col items-start px-0 lg:px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900 py-0">Permission Level</h3>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Select the permission level for this user on {{ companyInfo.name }}.
                        </p>
                    </div>
                </div>

                <div    v-if="isPermissionsLoaded() && permissions.can('edit_permission_level', companyInfo.id)"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="py-2 bg-white sm:p-6">
                            <div>
                                <permission-level-radio
                                    v-model="permissionLevel"
                                    :permission-levels="permissionLevels"
                                ></permission-level-radio>
                            </div>
                        </div>
                    </div>
                </div>

                <div    v-else-if="isPermissionsLoaded()"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="py-2 bg-white sm:p-6">
                            <div>
                                <span class="font-medium text-sm text-gray-700">Current Permission Level</span>
                                <div class="mt-2">
                                    <span class="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-blue-100 text-blue-800">
                                        {{ permissionLevels[permissionLevel]['displayName'] }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div    v-else
                        class="flex flex-col items-start mt-5 md:mt-0 md:col-span-3">
                    <loading-block  margin="my-1"
                    ></loading-block>
                </div>
            </div>
        </div>
        <div    v-if="permissions.can('create_timed_user_permissions', companyInfo.id) && this.hasSite && !this.isOnSingleSitePage"
            class="block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <!-- Timed User Permissions -->
        <div    v-if="permissions.can('create_timed_user_permissions', companyInfo.id) && this.hasSite && !this.isOnSingleSitePage"
                class="mt-4 sm:mt-0">
            <div class="md:grid md:grid-cols-4 md:gap-6">
                <div class="lg:pl-8 md:col-span-1">
                    <div class="flex flex-col items-start px-0 lg:px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900 py-0">Timed User Permissions</h3>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Enable auto-revocation of user permissions after a set time period
                        </p>
                    </div>
                </div>

                <div    class="mt-5 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="px-0 py-2 bg-white sm:p-6">
                            <div class="mt-1 sm:mt-0 sm:col-span-2">
                                <span class="relative z-0 inline-flex shadow-sm rounded-md">
                                    <button     type="button"
                                                @click="toggleTimedUserPermissionsEnabled(true)"
                                                :class="timedUserPermissionsEnabled ? 'bg-blue-200' : 'bg-white'"
                                                class="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                        Enabled
                                    </button>
                                    <button     type="button"
                                                @click="toggleTimedUserPermissionsEnabled(false)"
                                                :class="!timedUserPermissionsEnabled ? 'bg-blue-200' : 'bg-white'"
                                                class="-ml-px relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                                        Disabled
                                    </button>
                                </span>
                                <div v-if="timedUserPermissionsEnabled">
                                    <div class="col-span-6 sm:col-span-4">
                                        <label for="timed-user-permissions-enabled-days" class="block text-sm font-medium text-gray-700">Days to Allow Permissions</label>
                                        <input type="text"
                                               name="timed-user-permissions-enabled-days"
                                               id="timed-user-permissions-enabled-days"
                                               v-model="timedUserPermissionsDays"
                                               placeholder="Enter '1' or more days"
                                               class="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div    v-if="canEditUser"
                class="block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <!-- Add Role To User -->
        <div    v-if="permissions.can('add_role_to_user', companyInfo.id)"
                class="mt-4 sm:mt-0">
            <div class="md:grid md:grid-cols-4 md:gap-6">
                <div class="lg:pl-8 md:col-span-1">
                    <div class="flex flex-col items-start px-0 lg:px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900 py-0">Add Role To User</h3>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Sometimes additional roles are required for a user to interact with certain
                            specialized pages.
                        </p>
                    </div>
                </div>

                <div    v-if="permissions.can('edit_permission_level', companyInfo.id)"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="px-0 py-0 bg-white sm:p-6">
                            <div    v-if="permissions.can('add_role_to_user', companyInfo.id)">
                                <button-blue-secondary      @click="addRoleToUser"
                                >
                                    <span>Add Role To User</span>
                                </button-blue-secondary>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div    v-if="permissions.can('add_role_to_user', companyInfo.id)"
                class="block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <!-- Setup Notifications for this company -->
        <div    v-if="hasSite"
                class="mt-4 sm:mt-0">
            <div class="md:grid md:grid-cols-4 md:gap-6">
                <div class="lg:pl-8 md:col-span-1">
                    <div class="flex flex-col items-start px-0 lg:px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900 py-0">Notifications</h3>
                        <div>
                            <!-- Notifications Enabled Notes -->
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                User will receive alarm notifications.
                            </p>

                            <!-- Notification methods Notes -->
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                In the event of an alarm, you will receive a notification to each of the
                                listed methods.
                            </p>
                            <p class="mt-1 text-sm leading-5 text-gray-600">
                                If a user has recently logged into the SoniCloud Mobile app and accepted push notifications,
                                they can receive push notifications. Admins cannot <i>force</i> this onto users due to
                                iOS/Android restrictions. As users can delete SoniCloud Mobile from their devices,
                                <b>Sonicu highly recommends that users have one or more non-push notification methods</b>.
                            </p>
                        </div>
                    </div>
                </div>
                <div    v-if="isNotificationsLoaded"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div    class="px-0 lg:px-4 py-2 bg-white sm:p-6">
                            <user-setup-notifications
                                v-model:currentNotificationSettings="userNotificationSettings"
                                :user-id="userId"
                                :company-id="companyInfo.id"
                                :un-snooze-notification-enabled="unSnoozeNotificationEnabled"
                                :resolved-alarm-notifications-enabled="resolvedAlarmNotificationsEnabled"
                                :mandatory-resolved-alarm-notifications="mandatoryResolvedAlarmNotifications"
                                :require-alarm-comment="requireAlarmComment"
                            ></user-setup-notifications>
                        </div>
                    </div>
                </div>
                <div    v-else
                        class="mt-5 md:mt-0 md:col-span-3">
                    <loading-block      margin="my-1"
                    ></loading-block>
                </div>
            </div>
        </div>

        <div v-if="hasSite && canViewOrEditGroups" class="block">
            <div class="py-5">
                <div class="border-t border-gray-200"></div>
            </div>
        </div>

        <!-- Group Configuration -->
        <div    v-if="hasSite && canViewOrEditGroups"
                class="mt-4 sm:mt-0">
            <div class="md:grid md:grid-cols-4 md:gap-6">
                <div class="lg:pl-8 md:col-span-1">
                    <div class="flex flex-col items-start px-0 lg:px-4 sm:px-0">
                        <h3 class="text-lg font-medium leading-6 text-gray-900 py-0">Groups</h3>
                        <p class="mt-1 text-sm leading-5 text-gray-600">
                            Setup Groups the user has access to on {{ companyInfo.name }}.
                        </p>
                    </div>
                </div>
                <div    v-if="permissions.can('edit_groups')"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="px-0 lg:px-4 py-2 bg-white sm:p-6">
                            <h5 class="text-left text-sm font-semibold text-gray-800 mb-3">Groups</h5>
                            <select2
                                :multiple="true"
                                name="groupSelect"
                                :options="groupsList"
                                v-model:value="groups"
                            ></select2>
                        </div>
                    </div>
                </div>
                <div    v-else-if="permissions.can('view_groups')"
                        class="mt-2 md:mt-0 md:col-span-3">
                    <div class="overflow-hidden">
                        <div class="px-0 lg:px-4 py-2 bg-white sm:p-6">
                            <h5 class="text-left text-sm font-semibold text-gray-800 mb-3">Groups</h5>
                            <div class="flex items-start flex-wrap">
                                <div v-for="groupId in groups" :key="groupId">
                                    <span class="inline-flex items-center m-1 px-2.5 py-0.5 rounded-md text-md font-medium bg-blue-100 text-blue-800">
                                        {{ groupsList[groupId] }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';

import PermissionLevelRadio from "@/components/Users/PermissionLevelRadio";
import UserSetupNotifications from "@/components/Users/UserSetupNotifications";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary";
import Select2 from "@/components/elements/select2"
import Loading from "@/components/elements/loading";
import LoadingBlock from "@/components/elements/LoadingBlock";

export default {
    name: "UserEditCompanyInformation",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {LoadingBlock, Loading, ButtonBlueSecondary, Select2, PermissionLevelRadio, UserSetupNotifications},
    props: {
        userId: Number,
        companyInfo: Object,
        save: Boolean,
        unSnoozeNotificationEnabled: Boolean,
        showTimedUserPermission: {
            type: Boolean,
            default: false,
        },
        isEditingSelf: {
            type: Boolean,
            default: false,
        },
        shouldCreateCompanyAccess: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            // Company Specific
            hasSite: false,
            permissionLevels: [],
            permissionLevel: '',

            // Notifications
            userNotificationSettings: {
                loading: true,
                enabled: false,
                methods: {
                    email: [],
                    text: [],
                    voice: [],
                    push: [],
                },
                times: {},
                serverTimes: {},
                timesLoaded: false,
                timeChoice: '24/7',
                renotificationTime: 0,
                delayTime: 0,
                unsnoozeNotificationOptions: 'do_not_notify',
                notifyOnResolved: false,
            },

            // Resolved Alarm Notifications (company-level feature + mandatory flags)
            resolvedAlarmNotificationsEnabled: false,
            mandatoryResolvedAlarmNotifications: false,
            requireAlarmComment: false,

            // Timed User Permissions
            timedUserPermissionsEnabled: false,
            timedUserPermissionsDays: null,

            // Groups
            groupsList: null,
            groups: [],
        }
    },
    created() {
    },
    mounted() {


        axios.get(this.company.buildCompanyApiInternalRoute(`users/${this.userId}/edit`, this.companyInfo.id)
        ).then(response => {
            let data = response.data
            this.hasSite = data.company.has_site === 1

            // We are Editing a users current access to the company
            this.permissionLevels = data.permissions
            this.permissionLevel = data.user.permission_level_name
            this.groupsList = data.groups
            this.userNotificationSettings.renotificationTime = data.renotification_time;
            this.userNotificationSettings.delayTime = data.delay_time;
            this.userNotificationSettings.unsnoozeNotificationType = data.unsnooze_notification_type;
            setTimeout(() => {
                this.groups = data.user.groups
            }, 200)
        }).catch(error => {

        }).then(() => {
        })

        this.userNotificationSettings.loading = true

        // Load Notification Methods
        axios.get(this.company.buildCompanyApiInternalRoute(`notification-methods/${this.userId}`, this.companyInfo.id)
        ).then(response => {
            let data = response.data
            if (_.isNil(data)) {
                return
            }
            this.initializeNotificationSettings(data)
        }).catch(response => {
        }).then(() => {
            this.userNotificationSettings.loading = false
        })

        // Load Notification Times
        this.userNotificationSettings.timesLoaded = false
        axios.get(this.company.buildCompanyApiInternalRoute(`notification-times/${this.userId}`, this.companyInfo.id), {
            params: {},
        }).then(response => {
            let data = response.data
            this.userNotificationSettings.times = data.times
            this.userNotificationSettings.timeChoice = data.timeChoice
        }).catch(error => {
        }).then(() => {
            this.userNotificationSettings.timesLoaded = true
        })
    },
    methods: {
        saveInfo() {
            if (!this.canEditUser && !this.isEditingSelf) {
                this.$emit('update:save', 'Unable to Edit User')
                return
            }

            let isSaved = {
                user: false,
                methods: false,
                times: false,
                timedUserPermissions: false
            }

            axios.put(this.company.buildCompanyApiInternalRoute(`users/${this.userId}`, this.companyInfo.id),{
                companyInfoOnly: true,
                shouldCreateCompanyAccess: this.shouldCreateCompanyAccess,
                permissionLevel: this.permissionLevel,
                groups: this.groups,
            }).then(response => {
                let data = response.data.data
                this.permissionLevel = data.permission_level_name
                this.groups = data.groups

                isSaved.user = true
                this.checkAllSaved(isSaved)

                if (!this.hasSite) {
                    return
                }

                isSaved.methods = false
                // Begin Saving the methods & times after saving the main user.
                axios.put(this.company.buildCompanyApiInternalRoute(`notification-methods/${this.userId}`, this.companyInfo.id), {
                    settings: this.userNotificationSettings,
                }).then(response => {
                    let data = response.data
                    if (_.isNil(data)) {
                        return
                    }

                    this.initializeNotificationSettings(data)

                    isSaved.methods = true
                    this.checkAllSaved(isSaved)
                }).catch(error => {
                    console.log(`Error Saving Notification methods: ${error}`)
                }).then(() => {
                })

                // Begin Saving the timed user permissions
                if(this.timedUserPermissionsEnabled){
                    axios.post(this.company.buildCompanyApiInternalRoute(`users/${this.userId}/timed-user-permissions`, this.companyInfo.id), {
                        days: this.timedUserPermissionsDays,
                    }).then(response => {
                    }).catch(error => {
                        console.log(`Error Saving TimedUserPermissions: ${error}`)
                    }).then(() => {
                    })
                }
                isSaved.timedUserPermissions = true

                // If notifications are not enabled, there is no need to submit these requests.
                if (!this.userNotificationSettings.enabled) {
                    isSaved.times = null
                    this.checkAllSaved(isSaved)
                    return
                }

                isSaved.times = false
                // Save Notification Times
                axios.put(this.company.buildCompanyApiInternalRoute(`notification-times/${this.userId}`, this.companyInfo.id), {
                    times: this.userNotificationSettings.serverTimes,
                    enabled: this.userNotificationSettings.enabled,
                }).then(response => {
                    let data = response.data
                    if (_.isNil(data)) {
                        return
                    }

                    let times = data.times
                    Object.keys(times).forEach(day => {
                        let dayTimes = times[day]
                        dayTimes.forEach((interval, index) => {
                            interval.start = _.isNumber(interval.start) ? this.makeDateFromHour(interval.start) : interval.start
                            interval.end =  _.isNumber(interval.end) ? this.makeDateFromHour(interval.end) : interval.end
                            times[day][index] = interval
                        })
                    })

                    this.userNotificationSettings.times = times
                    this.userNotificationSettings.timeChoice = data.timeChoice

                    isSaved.times = true
                    this.checkAllSaved(isSaved)
                    // this.initializeNotificationSettings(data)
                }).catch(error => {
                    console.log(`Error Saving Notification times: ${error}`)
                }).then(() => {
                })
            }).catch(error => {
                console.log(error)
                this.handleValidationError(error)
                this.$emit('update:save', false)
            }).then(() => {
            })

        },
        initializeNotificationSettings(data) {
            if (!_.isNil(data.methods)) {
                // Ensure that the methods all have a value.
                let methodTypes = ['email', 'text', 'voice', 'push']
                methodTypes.forEach(item => {
                    if (_.has(data.methods, item)) {
                        this.userNotificationSettings.methods[item] = data.methods[item]
                    } else {
                        this.userNotificationSettings.methods[item] = []
                    }
                })
            }

            if (!_.isNil(data.enabled)) {
                this.userNotificationSettings.enabled = data.enabled
            }

            if (!_.isNil(data.notify_on_resolved)) {
                this.userNotificationSettings.notifyOnResolved = !!data.notify_on_resolved
            }

            if (!_.isNil(data.resolved_alarm_notifications_enabled)) {
                this.resolvedAlarmNotificationsEnabled = !!data.resolved_alarm_notifications_enabled
            }

            if (!_.isNil(data.mandatory_resolved_alarm_notifications)) {
                this.mandatoryResolvedAlarmNotifications = !!data.mandatory_resolved_alarm_notifications
            }

            if (!_.isNil(data.require_alarm_ack_comment)) {
                this.requireAlarmComment = !!data.require_alarm_ack_comment
            }
        },
        addRoleToUser() {
            let url = this.company.buildCompanyRoute(`${this.userId}/addroletouser`, this.companyInfo.id)
            window.open(url, '_blank')
        },
        checkAllSaved(isSaved) {
            let allSaved = true
            Object.keys(isSaved).forEach(key => {
                if (isSaved[key] === false) {
                    allSaved = false
                }
            })

            if (allSaved) {
                this.$emit('update:save', false)
                this.loading = false
            }
        },
        makeDateFromHour(hour) {
            return new Date(0, 0, 0, hour)
        },
        isPermissionsLoaded() {
            return Object.keys(this.permissionLevels).length > 0 && this.permissionLevel !== ''
        },
        toggleTimedUserPermissionsEnabled(newState) {
            this.timedUserPermissionsEnabled = newState;
        },
        handleValidationError(error) {
            if (error.response && error.response.status === 422) {
                const errors = error.response.data.errors || {}
                console.log('Validation errors:', errors)
            }
        }
    },
    computed: {
        isNotificationsLoaded() {
            return this.hasSite && !this.userNotificationSettings.loading
        },
        isGroupsLoaded() {
            return this.hasSite && Object.keys(this.groupsList).length > 0
        },
        canEditUser() {
            return this.permissions.can('update_users', this.companyInfo.id)
        },
        canViewOrEditGroups() {
            return this.permissions.can('edit_groups', this.companyInfo.id)
                || this.permissions.can('view_groups', this.companyInfo.id)
        },
        isOnSingleSitePage() {
            return this.company.getCompanyId() === this.companyInfo.id;
        },
    },
    watch: {
        save(val, old) {
            if (val !== old && val === true) {
                this.saveInfo()
            }
        }
    },
    beforeUnmount() {
        this.$emit('update:save', false)
    },
}
</script>

<style scoped>

</style>